/**
 * MaintenancePage — Full-screen overlay
 *
 * type="deploying"  → triggered by REACT_APP_MAINTENANCE_MODE=true
 * type="api-down"   → triggered by useApiHealthStore.apiDown
 * type="offline"    → triggered by navigator.onLine=false
 */
import React, { useEffect, useState } from 'react';

type MaintenanceType = 'deploying' | 'api-down' | 'offline';

interface MaintenancePageProps {
  type?: MaintenanceType;
}

interface Config {
  icon: string;
  title: string;
  titleEn: string;
  desc: string;
  descEn: string;
  color: string;
  hint?: string;
  hintEn?: string;
}

const CONFIGS: Record<MaintenanceType, Config> = {
  deploying: {
    icon: '🔧',
    title: 'ระบบกำลังปรับปรุง',
    titleEn: 'System Maintenance',
    desc: 'เรากำลังอัปเกรดระบบเพื่อให้คุณได้รับประสบการณ์ที่ดียิ่งขึ้น กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
    descEn: "We're upgrading the system for a better experience. Please wait a moment and try again.",
    color: '#038fde',
    hint: 'การปรับปรุงนี้ใช้เวลาสั้น ๆ ขอบคุณที่รอ',
    hintEn: 'This maintenance will be brief. Thank you for your patience.',
  },
  'api-down': {
    icon: '⚡',
    title: 'ไม่สามารถเชื่อมต่อระบบได้',
    titleEn: 'Connection Error',
    desc: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ในขณะนี้ ระบบจะพยายามเชื่อมต่อใหม่โดยอัตโนมัติ',
    descEn: 'Unable to reach the server right now. The system will retry automatically.',
    color: '#ff7a45',
    hint: 'หากปัญหายังคงอยู่ กรุณาติดต่อผู้ดูแลระบบ',
    hintEn: 'If the issue persists, please contact your system administrator.',
  },
  offline: {
    icon: '📡',
    title: 'ไม่มีการเชื่อมต่ออินเทอร์เน็ต',
    titleEn: 'No Internet Connection',
    desc: 'ดูเหมือนว่าอุปกรณ์ของคุณไม่ได้เชื่อมต่ออินเทอร์เน็ต กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่',
    descEn: 'Your device appears to be offline. Please check your connection and try again.',
    color: '#722ed1',
  },
};

const MaintenancePage: React.FC<MaintenancePageProps> = ({ type = 'deploying' }) => {
  const [dots, setDots] = useState('');
  const [elapsed, setElapsed] = useState(0);

  // Animated ellipsis for the title
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 600);
    return () => clearInterval(t);
  }, []);

  // Elapsed seconds counter (useful for api-down to show "been waiting X s")
  useEffect(() => {
    if (type !== 'api-down') return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [type]);

  const cfg = CONFIGS[type];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(135deg, #f0f7ff 0%, #fafbfc 50%, #f0f4f8 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '24px 20px',
        fontFamily:
          "'IBM Plex Sans Thai', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* Animated concentric rings */}
      <div style={{ position: 'relative', marginBottom: 32 }}>
        {/* Outer ring */}
        <div
          style={{
            width: 128,
            height: 128,
            borderRadius: '50%',
            border: `2px solid ${cfg.color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'maint-ring 2.4s ease-in-out infinite',
          }}
        >
          {/* Middle ring */}
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              border: `2px solid ${cfg.color}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'maint-ring 2.4s ease-in-out infinite reverse',
            }}
          >
            {/* Icon circle */}
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: '50%',
                background: `${cfg.color}12`,
                border: `2px solid ${cfg.color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                boxShadow: `0 0 24px ${cfg.color}30`,
              }}
            >
              {cfg.icon}
            </div>
          </div>
        </div>
      </div>

      {/* Brand label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 12,
        }}
      >
        <img
          src='./favicon.ico'
          alt='Fromas'
          style={{ width: 20, height: 20, borderRadius: 4 }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 2,
            color: '#8c8c8c',
            textTransform: 'uppercase',
          }}
        >
          Fromas Cloud PMS
        </span>
      </div>

      {/* Thai title */}
      <h1
        style={{
          fontSize: 'clamp(18px, 4vw, 26px)',
          fontWeight: 700,
          color: '#1a1a2e',
          margin: '0 0 4px',
          textAlign: 'center',
          letterSpacing: -0.3,
        }}
      >
        {cfg.title}
        {dots}
      </h1>

      {/* English title */}
      <p
        style={{
          fontSize: 13,
          color: '#8c8c8c',
          margin: '0 0 16px',
          textAlign: 'center',
          fontWeight: 500,
        }}
      >
        {cfg.titleEn}
      </p>

      {/* Divider */}
      <div
        style={{
          width: 40,
          height: 3,
          borderRadius: 99,
          background: `linear-gradient(90deg, ${cfg.color}, ${cfg.color}60)`,
          marginBottom: 16,
        }}
      />

      {/* Description */}
      <p
        style={{
          fontSize: 14,
          color: '#595959',
          maxWidth: 380,
          textAlign: 'center',
          margin: '0 0 4px',
          lineHeight: 1.8,
        }}
      >
        {cfg.desc}
      </p>
      <p
        style={{
          fontSize: 12,
          color: '#adadad',
          maxWidth: 380,
          textAlign: 'center',
          margin: 0,
          lineHeight: 1.7,
        }}
      >
        {cfg.descEn}
      </p>

      {/* Hint (smaller) */}
      {cfg.hint && (
        <p
          style={{
            fontSize: 11,
            color: '#bfbfbf',
            maxWidth: 320,
            textAlign: 'center',
            margin: '12px 0 0',
          }}
        >
          {cfg.hint}
          {cfg.hintEn && <> · {cfg.hintEn}</>}
        </p>
      )}

      {/* Elapsed time for api-down */}
      {type === 'api-down' && elapsed > 5 && (
        <p
          style={{
            fontSize: 11,
            color: '#bfbfbf',
            margin: '8px 0 0',
          }}
        >
          กำลังรอ {elapsed} วินาที · Waiting {elapsed}s
        </p>
      )}

      {/* Animated dots indicator */}
      <div style={{ display: 'flex', gap: 8, marginTop: 28 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: cfg.color,
              animation: 'maint-dot 1.4s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
              opacity: 0.4,
            }}
          />
        ))}
      </div>

      {/* Reload button for api-down */}
      {(type === 'api-down' || type === 'offline') && (
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 24,
            padding: '8px 24px',
            background: cfg.color,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: `0 4px 12px ${cfg.color}40`,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => ((e.target as HTMLButtonElement).style.opacity = '0.85')}
          onMouseLeave={(e) => ((e.target as HTMLButtonElement).style.opacity = '1')}
        >
          ลองใหม่ · Retry
        </button>
      )}

      <style>{`
        @keyframes maint-ring {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50%       { transform: scale(1.06); opacity: 1; }
        }
        @keyframes maint-dot {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40%           { transform: translateY(-8px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MaintenancePage;
